import { Box, IconButton, makeStyles, Typography } from "@material-ui/core";
import { Close, KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import LyfeButton from "libs/react-common/src/Button/LyfeButton";
import SuccessfulRounded from "libs/react-common/src/Icons/SuccessfulRounded";
import CircularProgressWithLabel from "libs/react-common/src/Loading/CircularProgressWithLabel";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useDataMutation } from "../../hooks/useDataModule";
import { AgreementRunStatus } from "../../models/generated/graphql";
import { agreementExecutionTotalCount, agreementsExecution } from "../../store/agreements";
import { upload } from "../../store/upload";
import { OnAgreementRunSubscription } from "../agreements/agreement/types/queries.generated";
import { CANCEL_UPLOAD } from "../data/uploads/uploader/queries";
import { UploadStatus } from "@lyfegen-platform/react-common";

const AgreementRunProgressNotification = () => {
	const { t } = useTranslation();
	const { card, cardHeader, headerText, agreementStatusMessage, okButton, listItem, listItemContent, scrollBox } = useStyles();

	const [cancelUpload] = useDataMutation(CANCEL_UPLOAD);
	const [
		{
			status,
		},
		setUploadState,
	] = useRecoilState(upload);

	const [agreementsInExecution, setAgreementsExecutionState] = useRecoilState(agreementsExecution);
	const [agreementExecutionTotalCountIds, setAgreementsExecutionTotalCountIds] = useRecoilState(agreementExecutionTotalCount);

	const [isDisplayed, setIsDisplayed] = useState<boolean>(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const [isProgressing, setIsProgressing] = useState<boolean>(false);
	const [progress, setProgress] = useState<number | null>(null);
	const [executedAgreements, setExecutedAgreements] = useState<Array<OnAgreementRunSubscription["agreementRun"]>>([]);
	const [displayedAgreementNames, setDisplayedAgreementsNames] = useState<Array<string | null>>([]);
	let [totalAgreements, setTotalAgreements] = useState<number>(agreementExecutionTotalCountIds.length);

	function closeSnackbar() {
		cancelUpload();
		setIsDisplayed(false);
		setIsOpen(false);
		setDisplayedAgreementsNames([]);
	}

	function getNewProgress(numberOfCompleted: number, total: number) {
		if (numberOfCompleted <= total && total) {
			const currentProgress = Math.round((100 * numberOfCompleted) / total);
			return currentProgress === 0 ? 1 : currentProgress;
		} else return progress;
	}

	useEffect(() => {
		if (agreementsInExecution.length && progress !== null) {
			const uniqueAgreementsInExecution = [...new Map(agreementsInExecution.map((agreement) => [agreement.id, agreement])).values()];
			const newExecutedAgreements = uniqueAgreementsInExecution?.filter(
				(agreement) => agreement.status === AgreementRunStatus.Executed
			);

			setExecutedAgreements(newExecutedAgreements);
		}
	}, [agreementsInExecution, progress]);

	useEffect(() => {
		if (progress !== null && progress < 100 && totalAgreements) {
			setIsDisplayed(true);
		}

		if (progress === 100) {
			const executedAgreementsNames = executedAgreements.map((agreement) => agreement.name);
			setDisplayedAgreementsNames((currentValue) =>
				Array.from(new Set([...currentValue, ...executedAgreementsNames].filter(Boolean) as Array<string>))
			);
			setIsProgressing(false);
			setIsOpen(true);

			setAgreementsExecutionState([]);
			setAgreementsExecutionTotalCountIds([]);
			setExecutedAgreements([]);
			setProgress(null);
			setTotalAgreements(0);
		}
	}, [progress]);

	useEffect(() => {
		totalAgreements = agreementExecutionTotalCountIds.length;
		setTotalAgreements(totalAgreements);

		if (isProgressing) {
			const newProgress = getNewProgress(executedAgreements.length, totalAgreements);
			setProgress(newProgress);
		}
	}, [executedAgreements.length, agreementExecutionTotalCountIds, totalAgreements]);

	useEffect(() => {
		setIsProgressing(!!agreementExecutionTotalCountIds.length);
	}, [!!agreementExecutionTotalCountIds.length]);

	useEffect(() => {
		if (status === UploadStatus.Completing) {
			setUploadState((prev) => ({
				...prev,
				isDisplayed: false,
			}));
			setIsDisplayed(false);
		}
	}, [status, progress]);

	if (!isDisplayed) {
		return <></>;
	}

	return (
		<Box className={card}>
			<Box display="flex" className={cardHeader} justifyContent="space-between">
				<Box display="flex" className={`${cardHeader} ${headerText}`}>
					<Typography className={agreementStatusMessage}>
						{isProgressing ? t("running-agreements") + "..." : t("agreement-run-completed")}
					</Typography>
				</Box>
				<Box display="flex" className={cardHeader} width="20%" px={2} alignItems="center" justifyContent="center">
					{isProgressing && progress && (
						<CircularProgressWithLabel
							color="secondary"
							thickness={5}
							size="30px"
							labelSize="8px"
							variant="determinate"
							value={progress}
						/>
					)}
					{!isProgressing && (
						<>
							<IconButton color="inherit" size="small" onClick={() => setIsOpen(!isOpen)}>
								{!isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
							</IconButton>
							<IconButton
								color="inherit"
								size="small"
								style={{ marginRight: 12 }}
								onClick={() => closeSnackbar()}
								id="close-uploader"
							>
								<Close />
							</IconButton>
						</>
					)}
				</Box>
			</Box>
			{isOpen && (
				<>
					<Box className={scrollBox}>
						<ul style={{ paddingLeft: 24 }}>
							{displayedAgreementNames.map((agreement) => (
								<li className={listItem}>
									<div className={listItemContent}>
										<SuccessfulRounded />
										<span style={{ marginLeft: 8 }}>{agreement}</span>
									</div>
								</li>
							))}
						</ul>
					</Box>
					<div className={okButton}>
						<LyfeButton variant="outlined" color="primary" style={{ marginRight: 24 }} onClick={() => closeSnackbar()}>
							{t("ok")}
						</LyfeButton>
					</div>
				</>
			)}
		</Box>
	);
};

export default AgreementRunProgressNotification;

const useStyles = makeStyles(({ spacing, palette }) => ({
	card: {
		boxShadow: "0 6px 10px 0 rgba(0, 0, 0, 0.08)",
		width: "420px",
		backgroundColor: palette.secondary.main,
		zIndex: 2,
	},
	cardHeader: {
		height: "52px",
		backgroundColor: palette.mainText.main,
		color: "white",
		alignItems: "center",
		borderRadius: "5px 5px 0px 0px",
	},
	headerText: {
		paddingLeft: spacing(3),
	},
	agreementStatusMessage: {
		fontSize: "14px",
		fontWeight: 600,
	},
	okButton: {
		textAlign: "right",
		backgroundColor: "#f4f8f9",
		padding: "8px 0",
	},
	listItem: { listStyle: "none", padding: "6px 0" },
	listItemContent: { display: "flex", alignItems: "center" },
	scrollBox: { maxHeight: 188, overflow: "auto" },
}));
